import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { highestRole } from "./roles";

const baseURL = `${process.env.REACT_APP_API_BASE_URL}/api/auth/v2`;
const authApi = axios.create({ baseURL, withCredentials: true });
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const REFRESH_AHEAD_MS = 5 * 60 * 1000;
const ACTIVE_ROLE_KEY = "demandSupplyActiveRole";

export function getToken() { return localStorage.getItem("token"); }
export function getAccessToken() { return localStorage.getItem("accessToken"); }

export function getCurrentUser() {
  const token = getToken();
  if (!token) return null;
  try {
    const claims = jwtDecode(token);
    const groups = claims["cognito:groups"] || [];
    let selectedRole;
    try {
      const saved = JSON.parse(sessionStorage.getItem(ACTIVE_ROLE_KEY) || "null");
      if (saved?.sub === claims.sub && groups.includes(saved.role)) selectedRole = saved.role;
    } catch { /* An invalid saved preference falls back to the highest role. */ }
    return { id: claims["cognito:username"], sub: claims.sub, name: claims.name || claims["cognito:username"], email: claims.email || "", groups, role: selectedRole || highestRole(groups), exp: claims.exp };
  } catch { return null; }
}

export function isSuperAdmin() {
  return ["SuperAdmin", "SustainAdmin"].includes(getCurrentUser()?.role);
}

export function setActiveRole(role) {
  const user = getCurrentUser();
  if (!user?.groups.includes(role)) return false;
  sessionStorage.setItem(ACTIVE_ROLE_KEY, JSON.stringify({ sub: user.sub, role }));
  return true;
}

export function storeSession(data) {
  localStorage.setItem("token", data.token);
  localStorage.setItem("accessToken", data.accessToken);
  localStorage.setItem("username", data.user?.id || "");
  axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
  window.dispatchEvent(new CustomEvent("auth-session", { detail: { authenticated: true } }));
  return data;
}

export function clearSession(reason = "signed-out") {
  ["token", "accessToken", "username", "loggedInUserDetails"].forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem(ACTIVE_ROLE_KEY);
  delete axios.defaults.headers.common.Authorization;
  window.dispatchEvent(new CustomEvent("auth-session", { detail: { authenticated: false, reason } }));
}

export async function login(credentials) {
  const { data } = await authApi.post("/login", credentials);
  sessionStorage.removeItem(ACTIVE_ROLE_KEY);
  return storeSession(data);
}

export async function refreshSession() {
  const { data } = await authApi.post("/refresh");
  return storeSession(data);
}

export async function logout(reason = "signed-out") {
  try { await authApi.post("/logout"); } finally { clearSession(reason); }
}

export function authRequest() {
  const client = axios.create({ baseURL, withCredentials: true, headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {} });
  client.interceptors.response.use((response) => response, (error) => {
    if (String(error.config?.method || "").toLowerCase() === "get") window.dispatchEvent(new CustomEvent("view-load-error", { detail: error.response?.data?.error || "An error occurred while loading this view." }));
    return Promise.reject(error);
  });
  return client;
}

export function startSessionManager({ onAuthenticated, onLogout } = {}) {
  let lastActivity = Date.now();
  let refreshing = false;
  const activityEvents = ["mousedown", "keydown", "touchstart", "scroll"];
  const noteActivity = () => { lastActivity = Date.now(); };
  activityEvents.forEach((event) => window.addEventListener(event, noteActivity, { passive: true }));

  const check = async () => {
    if (Date.now() - lastActivity >= IDLE_TIMEOUT_MS) {
      await logout("idle");
      onLogout?.("idle");
      return;
    }
    const user = getCurrentUser();
    if (!user || user.exp * 1000 - Date.now() > REFRESH_AHEAD_MS || refreshing) return;
    refreshing = true;
    try { await refreshSession(); onAuthenticated?.(); }
    catch { clearSession("expired"); onLogout?.("expired"); }
    finally { refreshing = false; }
  };

  const interval = window.setInterval(check, 60 * 1000);
  return () => {
    window.clearInterval(interval);
    activityEvents.forEach((event) => window.removeEventListener(event, noteActivity));
  };
}

export default authApi;
